import {
  ChatBubbleBottomCenterTextIcon,
  CursorArrowRaysIcon,
  EyeIcon,
  MegaphoneIcon,
  ShoppingBagIcon,
} from '@heroicons/react/20/solid'
import type { CampaignObjective } from '../../client'
import imgAwareness from '../../assets/meta/awareness.png'
import imgDefault from '../../assets/meta/default.png'
import imgEngagement from '../../assets/meta/engagement.png'
import imgSales from '../../assets/meta/sales.png'
import imgTraffic from '../../assets/meta/traffic.png'

const CHOICE = 'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition'
const CHOICE_ON = 'border border-indigo-300 bg-indigo-50 font-medium text-indigo-700'
const CHOICE_OFF = 'border border-transparent text-gray-700 hover:bg-gray-50'

const OBJECTIVES = [
  {
    value: 'OUTCOME_AWARENESS',
    label: 'Awareness',
    Icon: EyeIcon,
    image: imgAwareness,
    description: 'Show your ads to people who are most likely to remember them.',
    goodFor: ['Reach', 'Brand awareness', 'Video views'],
  },
  {
    value: 'OUTCOME_TRAFFIC',
    label: 'Traffic',
    Icon: CursorArrowRaysIcon,
    image: imgTraffic,
    description: 'Send people to a destination, like your website, app or Facebook event.',
    goodFor: ['Link clicks', 'Landing page views'],
  },
  {
    value: 'OUTCOME_ENGAGEMENT',
    label: 'Engagement',
    Icon: ChatBubbleBottomCenterTextIcon,
    image: imgEngagement,
    description: 'Get more messages, video views, post engagement, page likes or event responses.',
    goodFor: ['Video views', 'Post engagement', 'Interactions'],
  },
  {
    value: 'OUTCOME_SALES',
    label: 'Sales',
    Icon: ShoppingBagIcon,
    image: imgSales,
    description: 'Find people likely to purchase your product or service.',
    goodFor: ['Conversions', 'Catalog sales', 'Website purchases'],
  },
] satisfies { value: CampaignObjective; label: string; Icon: typeof EyeIcon; image: string; description: string; goodFor: string[] }[]

type Props = {
  objective: CampaignObjective
  campaignName: string
  onObjectiveChange: (objective: CampaignObjective) => void
  onCampaignNameChange: (name: string) => void
}

export default function CampaignObjectiveSelector({
  objective,
  campaignName,
  onObjectiveChange,
  onCampaignNameChange,
}: Props) {
  const selected = OBJECTIVES.find(({ value }) => value === objective)

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:h-[300px] md:flex-row">
        <div className="shrink-0 space-y-3 md:w-64">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Buying type</label>
            <button type="button" className={`${CHOICE} ${CHOICE_ON}`}>
              <MegaphoneIcon className="size-5 shrink-0 text-indigo-500" />
              <span>Auction</span>
            </button>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">Choose a campaign objective</label>
            <div className="space-y-1">
              {OBJECTIVES.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onObjectiveChange(value)}
                  className={`${CHOICE} ${objective === value ? CHOICE_ON : CHOICE_OFF}`}
                >
                  <Icon className={`size-5 shrink-0 ${objective === value ? 'text-indigo-500' : 'text-gray-400'}`} />
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden rounded-lg bg-gray-50">
          {selected ? (
            <>
              <div className="flex flex-1 items-center justify-center p-4">
                <img src={selected.image} alt={selected.label} className="size-32 shrink-0 object-contain" />
              </div>
              <div className="p-4 pt-0">
                <h4 className="mb-1 text-sm font-semibold text-gray-900">{selected.label}</h4>
                <p className="mb-2 text-xs text-gray-600">{selected.description}</p>
                <span className="mb-1.5 block text-xs font-medium text-gray-500">Good for:</span>
                <div className="flex flex-wrap gap-1.5">
                  {selected.goodFor.map((tag) => (
                    <span key={tag} className="rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-4 text-sm text-gray-400">
              <img src={imgDefault} alt="" className="mb-2 size-20 object-contain opacity-60" />
              Select an objective
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Campaign name <span className="ml-1 text-xs font-normal text-gray-400">[Campaign level]</span>
        </label>
        <input
          type="text"
          value={campaignName}
          placeholder="e.g. Summer Awareness Campaign"
          onChange={(event) => onCampaignNameChange(event.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
        />
      </div>

      <a
        href="https://www.facebook.com/business/help/1438417719786914"
        target="_blank"
        rel="noreferrer"
        className="text-xs text-indigo-600 hover:text-indigo-700"
      >
        About campaign objectives
      </a>
    </div>
  )
}
